const DEFAULT_SPEECH_LOCALE = "en-US"

const MAIN_READABLE_BLOCK_SELECTORS = [
  ".lexiqamus-site-page-content",
  ".lexiqamus-pricing-page",
  ".lexiqamus-institutional-subscribers-page",
  ".lexiqamus-access-denied-body",
  ".lexiqamus-innovations-wrap"
]

let cachedSpeechVoices = []
let speechSessionId = 0
let speechQueue = []
let currentUtterance = null

function refreshSpeechVoices() {
  cachedSpeechVoices = window.speechSynthesis?.getVoices() || []
}

export function primeSpeechVoices() {
  if (!window.speechSynthesis) return

  refreshSpeechVoices()
  if (window.__lexiqamusSpeechVoicesPrimed) return

  window.__lexiqamusSpeechVoicesPrimed = true
  window.speechSynthesis.addEventListener("voiceschanged", refreshSpeechVoices)
}

function normalizeSpeakText(text) {
  return (text || "").replace(/\s+/g, " ").trim()
}

function configureSpeechUtterance(utterance, lang) {
  const langPrefix = lang?.split("-")[0]
  const voice = cachedSpeechVoices.find((v) => v.lang === lang)
    || (langPrefix && cachedSpeechVoices.find((v) => v.lang.startsWith(`${langPrefix}-`)))
    || (langPrefix && cachedSpeechVoices.find((v) => v.lang.startsWith(langPrefix)))
    || cachedSpeechVoices.find((v) => v.default)
    || cachedSpeechVoices[0]

  if (voice) {
    utterance.voice = voice
    utterance.lang = voice.lang
  } else if (lang) {
    utterance.lang = lang
  }
}

function clearCurrentUtterance() {
  if (!currentUtterance) return
  currentUtterance.onend = null
  currentUtterance.onerror = null
  currentUtterance = null
}

function speakNextQueuedPart(sessionId, lang) {
  const synth = window.speechSynthesis
  if (!synth || sessionId !== speechSessionId || !speechQueue.length) return

  refreshSpeechVoices()
  const text = speechQueue.shift()
  if (synth.paused) synth.resume()

  const utterance = new SpeechSynthesisUtterance(text)
  configureSpeechUtterance(utterance, lang)
  const continueQueue = () => {
    if (sessionId !== speechSessionId) return
    speakNextQueuedPart(sessionId, lang)
  }
  utterance.onend = continueQueue
  utterance.onerror = continueQueue

  currentUtterance = utterance
  synth.speak(utterance)
}

export function stopSpeech() {
  const synth = window.speechSynthesis
  const shouldCancel = synth && (synth.speaking || synth.pending || speechQueue.length > 0)

  speechSessionId += 1
  speechQueue = []
  clearCurrentUtterance()

  if (!shouldCancel) return
  synth.cancel()
  synth.resume()
}

export function speakParts(parts, { lang = DEFAULT_SPEECH_LOCALE } = {}) {
  const queue = parts.filter(Boolean)
  const synth = window.speechSynthesis
  if (!queue.length || !synth) return

  const wasActive = synth.speaking || synth.pending || speechQueue.length > 0
  speechSessionId += 1
  speechQueue = queue
  clearCurrentUtterance()

  if (wasActive) {
    synth.cancel()
    synth.resume()
  }

  speakNextQueuedPart(speechSessionId, lang)
}

// ponytail: Chrome chokes on one long utterance; queue ~280-char chunks instead
export function chunkTextForSpeech(text, maxLength = 280) {
  const cleaned = normalizeSpeakText(text)
  if (!cleaned) return []
  if (cleaned.length <= maxLength) return [cleaned]

  const chunks = []
  for (let start = 0; start < cleaned.length;) {
    let end = Math.min(start + maxLength, cleaned.length)
    if (end < cleaned.length) {
      const spaceAt = cleaned.lastIndexOf(" ", end)
      if (spaceAt > start) end = spaceAt
    }
    const piece = cleaned.slice(start, end).trim()
    if (piece) chunks.push(piece)
    start = end
  }
  return chunks
}

export function mainReadableText() {
  const main = document.getElementById("lexiqamus-main")
  if (!main) return ""

  for (const selector of MAIN_READABLE_BLOCK_SELECTORS) {
    const text = main.querySelector(selector)?.innerText
    if (text?.trim()) return text
  }

  return main.innerText || ""
}
